import React, { useEffect, useState } from "react"
import { ModalName, TextMessage, TextMessageReport } from "sharedTypes"
import {
  Table,
  Tbody,
  Thead,
  Th,
  Tr,
  Td,
  Box,
  Flex,
  Text,
  Avatar,
} from "@chakra-ui/react"
import Link from "components/elements/Link"
import TextChannelIcon from "components/elements/TextChannelIcon"
import { eventPath, eventTextMessagePath } from "utilities/routes"
import { useHistory, useLocation, useParams } from "react-router-dom"
import FullScreenModalNav from "components/dialogs/FullScreenModalNav"
import Select from "components/ReactHookForm/Select"
import Button from "components/Buttons/Button"
import Modal from "components/dialogs/Modal"
import { asDate } from "utilities/strings"
import Icon from "components/elements/Icon"
import { useQuery } from "react-query"
import { useForm } from "react-hook-form"
import { getTextMessageReport } from "api/texts"
import _ from "lodash"
import Spinner from "components/elements/Spinner"
import useModal from "services/useModal"
import { STATUS_LABELS } from "utilities/textMessageStatusLabels"
import PaginationButtons from "components/PaginationButtons"

type Props = {
  textMessage: TextMessage
}

const TextReport = ({
  textMessage: {
    name,
    updatedAt,
    externalId,
    deliveryCounts,
  },
}: Props) => {
  const history = useHistory()
  const { eventId, textMessageId } = useParams<{ eventId: string, textMessageId: string }>()
  const [filter, setFilter] = useState("")
  const [page, setPage] = useState(1)
  const showModal = useModal()

  const FILTER_OPTIONS = [
    { label: "All Text Messages", value: "" },
    { label: "Sent", value: "sent" },
    { label: "Delivered", value: "delivered" },
    { label: "Delivery Unknown", value: "delivery_unknown" },
    { label: "Undelivered", value: "undelivered" },
    { label: "Failed", value: "failed" },
    { label: "Awaiting Opt-In", value: "awaiting_opt_in" },
    { label: "Opt-In Declined", value: "opt_in_declined" },
    { label: "Opt-In Expired", value: "opt_in_expired" },
  ]

  const close = () => {
    history.push(eventTextMessagePath(eventId!, externalId))
  }

  const onSubmit = (values) => {
    setFilter(values.filter)
  }

  const {
    data: report,
    isLoading,
  } = useQuery(
    ["textMessageReport", textMessageId, filter, page],
    () => getTextMessageReport(eventId!, textMessageId, page, filter).then(({ data }) => data),
  )

  const { control, handleSubmit, watch } = useForm({
    defaultValues: { filter },
  })

  const filterValue = watch("filter")

  useEffect(() => {
    handleSubmit(onSubmit)()
  }, [filterValue])

  const handleChangePage = (selectedPage: number) => {
    setPage(selectedPage)
  }

  const allRecipients = _.sum(Object.values(deliveryCounts))
  const totalPages = Math.ceil(allRecipients / 50)

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal
        show
        noPadding
        size="full"
        hide={close}
        header={(
          <FullScreenModalNav type="header">
            <Text>Text Report</Text>
            <Text>{name}: {asDate(updatedAt)}</Text>
            <Link onClick={close} color="blue.300" fontWeight="bold">
              Close
              <Icon icon="cancel-circle" ml={2} />
            </Link>
          </FullScreenModalNav>
          )}
      >
        <Box overflowY="scroll" p={5} my={12}>
          <Flex justify="space-between">
            <Box>
              <Select
                name="filter"
                control={control}
                options={FILTER_OPTIONS}
              />
            </Box>
            <Flex align="flex-start">
              {totalPages > 1
                && (
                  <Box mr={5}>
                    <PaginationButtons
                      currentPage={page}
                      pageCount={totalPages}
                      onChangePage={handleChangePage}
                      totalCount={allRecipients}
                      currentPageLength={(report || []).length}
                      showItemInfo
                    />
                  </Box>
                )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => showModal(ModalName.ExportTextReport,
                  { id: externalId })}
              >Export to CSV
              </Button>
            </Flex>
          </Flex>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Phone Number</Th>
                <Th>Channel</Th>
                <Th>Sent</Th>
                <Th>Delivery Status</Th>
              </Tr>
            </Thead>
            {isLoading ? <Tbody><Tr><Td colSpan={5}><Spinner /></Td></Tr></Tbody> : (
              <Tbody>
                { (report || []).map((reportItem) => (
                  <Row
                    key={reportItem.data.sid}
                    reportItem={reportItem}
                    eventId={eventId}
                  />
                ))}
              </Tbody>
            )}
          </Table>
        </Box>
      </Modal>
    </form>
  )
}

type RowProps = {
  reportItem: TextMessageReport
  eventId: string
}

const formatStatus = (status: string, fallbackStatus: string | null) => {
  const label = STATUS_LABELS[status] ?? _.capitalize(status)

  if (fallbackStatus === "sent") {
    return `${label} (SMS fallback sent)`
  }

  if (fallbackStatus === "skipped_no_credits") {
    return `${label} (no credits for SMS fallback)`
  }

  return label
}

const Row = ({
  reportItem: {
    guest: { id: guestId, name: guestName },
    data: {
      to, status, sid, channel,
    },
    createdAt,
    fallbackStatus,
  },
  eventId,
}: RowProps) => {
  const location = useLocation()
  const statusLabel = formatStatus(status, fallbackStatus)

  return (
    <Tr key={sid}>
      <Td>
        <Link
          to={{ pathname: `${eventPath(eventId)}/guests/${guestId}`, state: { background: location } }}
          color="inherit"
          _hover={{ textDecoration: "none" }}
        >
          <Flex alignItems="center" gap={2}>
            <Avatar size="sm" bg={`textStatus.${status}`} name={guestName} />{guestName}
          </Flex>
        </Link>
      </Td>
      <Td>{to}</Td>
      <Td>
        <Flex alignItems="center" gap={1.5}>
          <><TextChannelIcon channel={channel} size={4} /> {channel === "whatsapp" ? "WhatsApp" : "SMS"}</>
        </Flex>
      </Td>
      <Td>{asDate(createdAt)}</Td>
      <Td>
        <Flex alignItems="center" gap={1.5}>
          <Icon icon="dot" size={3} color={`textStatus.${status}`} />
          {statusLabel}
        </Flex>
      </Td>
    </Tr>
  )
}

export default TextReport
